/**
 * Camera permission state resolver.
 *
 * Why this helper exists:
 *   The bug fix for /scan's false "Camera permission denied" warning
 *   (2026-06-17) needed a single source of truth that distinguishes the
 *   three meaningful Permission API states — "granted", "denied",
 *   "prompt" — across browsers that disagree about how to ask.
 *
 *   * Chrome / Edge / Firefox / Android Chrome:
 *       navigator.permissions.query({name:"camera"}) returns the real
 *       PermissionStatus.
 *   * Safari (macOS) / iOS WebKit:
 *       navigator.permissions exists but query({name:"camera"})
 *       THROWS TypeError because the "camera" name is not in their
 *       permission registry. We fall back to "prompt" — the safest
 *       default that surfaces the friendly "tap Allow when asked"
 *       hint rather than a misleading "denied" message.
 *   * Old/embedded WebViews:
 *       navigator.permissions may be undefined entirely. Same
 *       fallback to "prompt".
 *
 * Returns a literal-typed string instead of the PermissionStatus
 * object so callers don't have to worry about lifecycle / change
 * subscription (the React component does that separately with
 * navigator.permissions.query directly).
 */
export async function resolveCameraPermissionState() {
  // SSR / unit tests without navigator
  if (typeof navigator === "undefined") return "prompt";
  if (!navigator.permissions || typeof navigator.permissions.query !== "function") {
    return "prompt";
  }
  try {
    const result = await navigator.permissions.query({ name: "camera" });
    const state = result && result.state;
    if (state === "granted" || state === "denied" || state === "prompt") {
      return state;
    }
    // Unknown state value — default to prompt rather than guess.
    return "prompt";
  } catch (_) {
    // Safari throws TypeError on { name: "camera" }.
    // Treat as "prompt" so the UI shows the friendly hint and the
    // subsequent getUserMedia call surfaces the native dialog.
    return "prompt";
  }
}
